/**
* Job Seeker Subscription Page
* Premium plan subscription for job seekers
*/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, TrendingUp, Award, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import RazorpayCheckout from '../../components/Payment/RazorpayCheckout';
import { getPlanDetails } from '../../services/razorpayService';
import { getFirestore, doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import toast from 'react-hot-toast';
import { useSubscription } from '../../hooks/useSubscription';

const db = getFirestore(firebaseApp);

const JobSeekerSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('1 month');
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const [notifyEmail, setNotifyEmail] = useState('');

  const plan = getPlanDetails('JOBSEEKER', selectedDuration);

  const planOptions = [
    { duration: '1 month', label: '1 Month', amount: 199, originalAmount: 299 },
    { duration: '3 months', label: '3 Months', amount: 549, originalAmount: 597 },
    { duration: '6 months', label: '6 Months', amount: 1179, originalAmount: 1194 },
    { duration: '12 months', label: '12 Months', amount: 2299, originalAmount: 2388 },
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        const userDoc = await getDoc(doc(db, 'jobseekers', user.id));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setIsPremium(data.isPremium || false);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  useEffect(() => {
    if (subscription?.status === 'active' && subscription?.plan !== 'free' && subscription?.plan !== 'none') {
      setIsPremium(true);
    }
  }, [subscription]);

  const handlePaymentSuccess = () => {
    toast.success('Subscription activated successfully!');
    setIsPremium(true);

    // Redirect to dashboard after 2 seconds
    setTimeout(() => {
      navigate('/job-seeker');
    }, 2000);
  };

  const handlePaymentFailure = (error) => {
    toast.error(error.message || 'Payment failed. Please try again.');
  };

  const isLoading = loading || subscriptionLoading;

  const formatDate = (date) => {
    if (!date) return 'Not set';
    const parsed = date instanceof Date ? date : new Date(date);
    return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const subscriptionStatus = subscription?.status || 'inactive';
  const subscriptionPlan = subscription?.plan || 'free';
  const expiresAt = subscription?.expiresAt ? formatDate(subscription.expiresAt) : 'Not set';
  const isActiveSub = subscription?.status === 'active';
  const expiryDisplay = subscription?.expiresAt ? expiresAt : (isActiveSub ? 'Lifetime' : null);
  const statusDot = subscriptionStatus === 'active' ? 'bg-green-500' : (subscriptionStatus === 'expired' ? 'bg-red-500' : 'bg-amber-500');

  const statusColor =
    subscriptionStatus === 'active'
      ? 'bg-green-100 text-green-800'
      : subscriptionStatus === 'expired'
        ? 'bg-red-100 text-red-800'
        : 'bg-yellow-100 text-yellow-800';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Unified "Choose Your Plan" layout like screenshot
  const isProActive = subscription?.status === 'active' && subscription?.plan !== 'free' && subscription?.plan !== 'none';

  const handleNotify = async () => {
    const email = (notifyEmail || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await addDoc(collection(db, 'proPlusNotify'), {
        email,
        role: 'JobSeeker',
        userId: user?.id || null,
        createdAt: serverTimestamp(),
      });
      toast.success('We will notify you when Pro Plus launches');
      setNotifyEmail('');
    } catch (e) {
      console.error('Notify save error:', e);
      toast.error('Failed to save notification preference');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 py-12 px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-[1920px] mx-auto relative z-10 px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold mb-4">
            Subscription Plans
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-3">Choose Your Plan</h1>
          <p className="text-lg text-gray-600">Select the perfect plan to unlock your career potential</p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-rose-100 text-slate-900 font-semibold shadow-sm border border-rose-200">
              <span className="h-2 w-2 rounded-full bg-rose-500" aria-hidden="true"></span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Plan</span>
              <span className="text-slate-900 font-semibold">{(subscriptionPlan && subscriptionPlan !== 'none') ? subscriptionPlan.charAt(0).toUpperCase() + subscriptionPlan.slice(1) : 'Free'}</span>
            </span>

            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-100 text-slate-900 font-semibold shadow-sm border border-green-200">
              <span className={`h-2 w-2 rounded-full ${statusDot}`} aria-hidden="true"></span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-green-800">Status</span>
              <span className="text-slate-900 font-semibold">{subscriptionStatus.charAt(0).toUpperCase() + subscriptionStatus.slice(1)}</span>
            </span>

            {expiryDisplay && (
              <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 text-slate-900 font-semibold shadow-sm border border-slate-200">
                <span className="h-2 w-2 rounded-full bg-slate-500" aria-hidden="true"></span>
                <span className="text-slate-900 font-semibold">Expires: {expiryDisplay}</span>
              </span>
            )}
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Basic */}
          <div className="bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-200 p-8 transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.25)] hover:-translate-y-2">
            <div className="inline-block px-4 py-1.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full mb-5">Free Forever</div>
            <h3 className="text-2xl font-bold text-gray-900">Basic</h3>
            <div className="mt-3 text-3xl font-extrabold">₹0<span className="text-lg font-medium text-gray-600">/month</span></div>
            <ul className="mt-8 space-y-4 text-base text-gray-700">
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Access to VetriaAI dashboard and skill badges</li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Limited assessments and job applications each month</li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Standard support through support tickets</li>
            </ul>
            {!isProActive && (
              <button className="mt-6 w-full bg-gray-100 text-gray-700 rounded-lg py-2.5 font-semibold" disabled>
                Current Plan
              </button>
            )}
          </div>

          {/* Pro (Most Popular) */}
          <div className="relative bg-white rounded-xl shadow-[0_15px_50px_rgba(220,38,38,0.25)] border-2 border-red-400 p-8 transition-all duration-300 hover:shadow-[0_25px_70px_rgba(220,38,38,0.35)] hover:-translate-y-2">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-red-700 text-white text-sm font-bold px-5 py-1.5 rounded-full shadow-lg">Most Popular</span>
            </div>

            <>
              <h3 className="text-2xl font-bold text-gray-900">Pro</h3>

              {/* Pricing Display */}
              <div className="mt-3">
                <div className="flex items-center gap-4">
                  <span className="text-xl font-medium text-gray-400 line-through">₹{plan.originalAmount}</span>
                  <span className="text-4xl font-extrabold text-red-700 bg-gradient-to-r from-red-700 via-red-500 to-red-700 bg-clip-text text-transparent animate-shimmer" style={{ backgroundSize: '200% auto' }}>₹{plan.amount}</span>
                </div>
                {selectedDuration !== '1 month' && (
                  <p className="text-base text-gray-600 font-medium mt-2">₹{Math.round(plan.amount / parseInt(selectedDuration))} per month</p>
                )}
                {selectedDuration === '1 month' && (
                  <p className="text-base text-gray-600 mt-2">Billed monthly</p>
                )}
              </div>

              {/* Duration Selector */}
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
                {planOptions.map((opt) => (
                  <button
                    key={opt.duration}
                    onClick={() => setSelectedDuration(opt.duration)}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedDuration === opt.duration ? 'border-red-600 bg-red-50 text-red-700 shadow-sm' : 'border-gray-200 hover:border-red-400 hover:shadow-sm'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <ul className="mt-8 space-y-4 text-base text-gray-700">
                <li className="flex items-start gap-2"><span className="text-red-700 font-bold">✓</span> Everything in Basic</li>
                <li className="flex items-start gap-2"><span className="text-red-700 font-bold">✓</span> Unlimited assessments and job recommendations</li>
                <li className="flex items-start gap-2"><span className="text-red-700 font-bold">✓</span> Personalized courses and learning paths</li>
                <li className="flex items-start gap-2"><span className="text-red-700 font-bold">✓</span> Advanced analytics on skills and progress</li>
                <li className="flex items-start gap-2"><span className="text-red-700 font-bold">✓</span> Priority support</li>
              </ul>
              {isProActive ? (
                <div className="mt-6 space-y-2">
                  <button className="w-full bg-green-100 text-green-800 rounded-lg py-2.5 font-bold text-sm border-2 border-green-300" disabled>
                    ✓ Current Plan
                  </button>
                  {user && userData && (
                    <RazorpayCheckout
                      amount={plan.amount}
                      planType="JOBSEEKER"
                      planDuration={plan.duration}
                      planName={plan.name}
                      userDetails={{
                        name: userData.name || user.email || 'User',
                        email: userData.email || user.email || '',
                        contact: userData.phone || '',
                      }}
                      onSuccess={handlePaymentSuccess}
                      onFailure={handlePaymentFailure}
                      buttonText="Extend Subscription"
                      buttonClassName="w-full bg-red-700 text-white hover:bg-red-800 transition-colors"
                    />
                  )}
                </div>
              ) : (
                user && userData && (
                  <div className="mt-6">
                    <RazorpayCheckout
                      amount={plan.amount}
                      planType="JOBSEEKER"
                      planDuration={plan.duration}
                      planName={plan.name}
                      userDetails={{
                        name: userData.name || user.email || 'User',
                        email: userData.email || user.email || '',
                        contact: userData.phone || '',
                      }}
                      onSuccess={handlePaymentSuccess}
                      onFailure={handlePaymentFailure}
                      buttonText="Subscribe Now"
                      buttonClassName="w-full bg-red-700 text-white hover:bg-red-800 transition-colors"
                    />
                  </div>
                )
              )}
            </>
          </div>

          {/* Premium (Coming Soon) */}
          <div className="relative group">
            {/* Animated Glowing Border Background */}
            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-orange-500 to-rose-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient bg-[length:200%_200%]"></div>

            {/* Card Content */}
            <div className="relative bg-white rounded-xl p-8 h-full shadow-xl ring-1 ring-gray-900/5">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">Premium</h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-primary to-red-700 text-white text-xs font-bold uppercase tracking-wide shadow-md shadow-red-200">Coming Soon</span>
                </div>

                <p className="mt-2 text-gray-600 text-base">The ultimate power for serious career growth.</p>

                {/* <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="p-1.5 rounded-full bg-red-50 text-primary border border-red-100"><Zap size={16} /></div>
                    <span className="text-sm font-semibold">AI-Powered Interview Coach</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="p-1.5 rounded-full bg-red-50 text-primary border border-red-100"><Award size={16} /></div>
                    <span className="text-sm font-semibold">Verified Gold Badge</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <div className="p-1.5 rounded-full bg-red-50 text-primary border border-red-100"><TrendingUp size={16} /></div>
                    <span className="text-sm font-semibold">Priority Job Placement</span>
                  </div>
                </div> */}

                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-xs text-primary font-bold mb-3 uppercase tracking-wider">Get Early Access</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                    <button
                      onClick={handleNotify}
                      className="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-bold hover:bg-red-800 transition-colors shadow-lg hover:shadow-primary/30"
                    >
                      Notify Me
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-3 font-medium">Be the first to know when Premium launches.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Can I cancel anytime?</h3>
              <p className="text-gray-600 text-base">Yes, you can cancel your subscription at any time. No questions asked.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">How do I access courses?</h3>
              <p className="text-gray-600 text-base">Once subscribed, courses will be automatically generated based on your test results.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Is my payment secure?</h3>
              <p className="text-gray-600 text-base">Yes, we use Razorpay's secure payment gateway. Your data is encrypted and protected.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">What payment methods are accepted?</h3>
              <p className="text-gray-600 text-base">We accept credit/debit cards, UPI, net banking, and digital wallets.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerSubscription;
